// Team Training — a dedicated section (separate from client Resources) where the
// team uploads + views training videos, PDFs and documents. Reuses the resources
// backend (onSaveResource/onDeleteResource) with a reserved category marker so
// these never mix with carrier/client-facing resources.
import { useState, useMemo, useRef, useEffect } from 'react';
import { GraduationCap, Plus, Upload, Trash2, Eye, Download, X } from 'lucide-react';
import { confirmDialog } from './ui.jsx';
import { toast } from './Toast.jsx';
import { uploadFileToBlob, freshResourceFileUrl } from '../api.js';
import { revisarArchivoDeVideo, textoDelAviso } from '../videoCompatible.js';
import { distinctLabels } from '../utils.js';
import VisorDeVideo from './VisorDeVideo.jsx';

export const TEAM_TRAINING_CATEGORY = 'Team Training';

// Compania y tipo de entrenamiento: los DOS son texto libre que se escribe una
// vez y queda guardado (Lissette, 2026-08-26: *"quitale esos nombres
// predeterminados, que sean personalizados y se vaya quedando guardado"*).
//
// La primera version traia una lista fija de temas (Proceso de pagos,
// Onboarding, Renovaciones ACA...) y ella la quito a proposito. Tenia razon:
// cada agencia entrena lo suyo, y un catalogo escrito por nosotros obliga a
// meter el material en la casilla que MENOS mal le queda — o a dejarlo sin
// clasificar. Aqui no se decide como se organiza una agencia.
//
// "Se vaya quedando guardado" es la otra mitad, y es la que hace que esto no se
// convierta en un campo de texto suelto: lo que alguien escribio una vez
// aparece como sugerencia para todos los demas, asi que la lista la construye
// el equipo con el uso. No hace falta guardarla en ningun sitio aparte — se
// deduce de los materiales que ya existen, que es la unica copia que no se
// puede desincronizar.
//
// El tema se guarda en `tags` (el campo que ya existe y que es, literalmente,
// lo que Gabriel pidio: "categorias/etiquetas") y la compania en `carrier`.
// Ninguno de los dos lo usaban los materiales de entrenamiento, y el backend ya
// los acepta: sin migracion.

/** El tema guardado en `tags`, o cadena vacia. */
export const topicOf = (r) => (Array.isArray(r?.tags) ? String(r.tags[0] || '') : '');
const MAX_FILE = 500 * 1024 * 1024; // 500MB — files go straight to Azure Blob.
const isBlobUrl = (u) => /blob\.core\.windows\.net/.test(String(u || ''));

// Open a data: URL safely (browsers block top-level navigation to data: URLs)
// by turning it into a short-lived blob URL.
function openDataUrl(dataUrl) {
  try {
    const arr = String(dataUrl).split(',');
    const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'application/octet-stream';
    const bin = atob(arr[1]); const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([u8], { type: mime }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { /* ignore */ }
}

const fmtSize = (b) => !b ? '' : b < 1024 ? b + ' B' : b < 1048576 ? (b / 1024).toFixed(1) + ' KB' : (b / 1048576).toFixed(1) + ' MB';
const typeIcon = (t) => ({ video: '🎬', pdf: '📕', image: '🖼️', document: '📄' }[t] || '📄');

export function TeamTrainingView({ resources, policies = [], onSaveResource, onDeleteResource, onResourcesChange, currentUser, lang }) {
  const es = lang === 'es';
  const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
  const [search, setSearch] = useState('');
  // Filtros de la vista principal (Gabriel De Silva 2026-08-26): hasta ahora
  // solo habia busqueda por nombre, y con la lista larga tocaba recorrerla a
  // ojo.
  const [filterCarrier, setFilterCarrier] = useState('all');
  const [filterTopic, setFilterTopic] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState(null); // { src, name } image lightbox
  // {src, nombre}: el nombre hace falta para el boton de descargar que sale
  // cuando el navegador no sabe reproducir el archivo (ver VisorDeVideo).
  const [videoUrl, setVideoUrl] = useState(null); // uploaded-video player
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const empty = { title: '', description: '', type: 'video', fileUrl: '', fileData: '', fileName: '', fileSize: 0, carrier: '', topic: '' };
  const [form, setForm] = useState(empty);
  const fileRef = useRef();
  const videoFileRef = useRef();

  const training = useMemo(
    () => (resources || []).filter(r => r.category === TEAM_TRAINING_CATEGORY),
    [resources],
  );

  // Lo que ya se ha usado, que es lo que se sugiere al escribir. Nada de
  // catalogos nuestros: las companias salen de las polizas REALES de esta
  // agencia y de los materiales que ella misma subio, y los temas solo de los
  // materiales — porque los temas se los inventa el equipo, no existen en
  // ningun otro sitio del CRM de donde sacarlos.
  const carrierOptions = useMemo(
    () => distinctLabels([...(policies || []).map(p => p.carrier), ...training.map(r => r.carrier)]),
    [policies, training],
  );
  const topicOptions = useMemo(() => distinctLabels(training.map(topicOf)), [training]);

  // Los FILTROS solo ofrecen lo que hay a la vista: un desplegable lleno de
  // opciones que no devuelven ni una fila es ruido. Ojo: se filtra por el texto
  // TAL CUAL se guardo, asi que aqui NO se puede deduplicar ignorando tildes y
  // mayusculas — la opcion tiene que ser identica a lo guardado o no casa nada.
  const carriersPresentes = useMemo(
    () => [...new Set(training.map(r => (r.carrier || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')),
    [training],
  );
  const temasPresentes = useMemo(
    () => [...new Set(training.map(topicOf).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')),
    [training],
  );

  // Un filtro puesto sobre algo que ya no esta se resetea solo; si no, la lista
  // se queda vacia y parece que no hay materiales.
  useEffect(() => {
    if (filterCarrier !== 'all' && !carriersPresentes.includes(filterCarrier)) setFilterCarrier('all');
  }, [carriersPresentes, filterCarrier]);
  useEffect(() => {
    if (filterTopic !== 'all' && !temasPresentes.includes(filterTopic)) setFilterTopic('all');
  }, [temasPresentes, filterTopic]);

  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    return training
      .filter(r => !q || `${r.title || ''} ${r.description || ''}`.toLowerCase().includes(q))
      .filter(r => filterCarrier === 'all' || (r.carrier || '') === filterCarrier)
      .filter(r => filterTopic === 'all' || topicOf(r) === filterTopic)
      .slice().reverse();
  }, [training, search, filterCarrier, filterTopic]);

  // Upload ANY file (incl. large videos) straight to Azure Blob and store the
  // URL — no 5MB cap because the bytes go browser → blob, not through our API.
  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_FILE) { toast.warn(es ? 'Archivo demasiado grande (máx 500MB).' : 'File too large (max 500MB).'); return; }
    if (!isApi) { toast.warn(es ? 'Sube archivos con tu cuenta conectada.' : 'Sign in to upload files.'); return; }
    setUploading(true); setProgress(0);
    try {
      const url = await uploadFileToBlob(file, (p) => setProgress(p));
      const t = file.type.startsWith('video/') ? 'video' : file.type.startsWith('image/') ? 'image' : file.type === 'application/pdf' ? 'pdf' : 'document';
      // El video se sube IGUAL; el aviso viene despues (ticket de Gabriel,
      // 2026-09-02). Frenar la subida perderia el archivo y obligaria a repetir
      // todo el rato de espera — y el video se puede descargar y ver, solo que
      // no dentro del navegador. Avisar sin bloquear es lo que respeta las dos
      // cosas: que se entere, y que no pierda el trabajo.
      if (t === 'video') {
        const revision = await revisarArchivoDeVideo(file);
        if (revision.avisar) toast.warn(textoDelAviso(revision, es), { duration: 15000 });
      }
      setForm(f => ({ ...f, fileUrl: url, fileData: '', fileName: file.name, fileSize: file.size, type: t }));
      toast.success(es ? 'Archivo subido' : 'File uploaded');
    } catch (e) { toast.error((es ? 'No se pudo subir: ' : 'Upload failed: ') + (e.message || e)); }
    finally { setUploading(false); }
  };

  const save = () => {
    if (uploading) { toast.warn(es ? 'Espera a que termine de subir.' : 'Wait for the upload to finish.'); return; }
    if (!form.title.trim()) { toast.warn(es ? 'Ponle un título' : 'Add a title'); return; }
    if (!form.fileUrl.trim()) { toast.warn(es ? 'Sube un archivo o pega el link del video' : 'Upload a file or paste a video link'); return; }
    const res = {
      // Must start with "res_" so saveResourceRow treats it as a CREATE (POST),
      // not an update (PUT) of a non-existent id.
      id: 'res_' + Date.now(),
      title: form.title.trim(), description: form.description.trim(),
      type: form.type, category: TEAM_TRAINING_CATEGORY, carrier: form.carrier.trim(), policyType: '',
      fileData: '', fileUrl: form.fileUrl.trim(), fileName: form.fileName, fileSize: form.fileSize,
      uploadedBy: currentUser?.id, uploadedAt: new Date().toISOString(),
      sharedWithClients: [], sharedWithAll: false,
      // El tema va en `tags`, recortado. Vacio => array vacio, para
      // no guardar un [''] que despues habria que filtrar en cada lectura.
      // El tema va en `tags`, recortado. Sin tema => array vacio, para no
      // guardar un [''] que despues habria que filtrar en cada lectura.
      tags: form.topic.trim() ? [form.topic.trim()] : [],
    };
    if (onSaveResource) onSaveResource(res);
    else if (onResourcesChange) onResourcesChange(prev => [...(prev || []), res]);
    setForm(empty); setShowForm(false);
    toast.success(es ? 'Material agregado' : 'Material added');
  };

  const del = async (r) => {
    const ok = await confirmDialog({ message: es ? `¿Borrar "${r.title}"? Esta acción no se puede deshacer.` : `Delete "${r.title}"? This cannot be undone.`, danger: true });
    if (!ok) return;
    if (onDeleteResource) onDeleteResource(r.id);
    else if (onResourcesChange) onResourcesChange(prev => (prev || []).filter(x => x.id !== r.id));
  };

  // Re-sign at the moment of opening (E-085). The `resources` array was signed
  // whenever the app last loaded it, so on a tab that has been open a while the
  // stored URL is already dead — a fresh one is seconds old.
  const view = async (r) => {
    if (!(r.fileUrl || r.fileData)) return;
    const needsTab = r.type !== 'image'
      && !(r.type === 'video' && (r.fileName || isBlobUrl(r.fileUrl)))
      && !String(r.fileUrl || r.fileData).startsWith('data:');
    // A tab opened after `await` is a popup, not a click — browsers block it.
    // Claim it synchronously while we still hold the user gesture, then point
    // it at the signed URL once we have one. No 'noopener' here: with it the
    // call returns null, so we'd lose the handle and strand a blank tab while
    // opening a second one. Severing `opener` by hand gives the same guarantee.
    const tab = needsTab ? window.open('about:blank', '_blank') : null;
    if (tab) { try { tab.opener = null; } catch { /* cross-origin, already safe */ } }

    const src = await freshResourceFileUrl(r);
    if (!src) { tab?.close(); return; }
    // These three render in-app, so hand back the tab if we claimed one — the
    // signed URL and the stored one can disagree about which branch applies.
    if (r.type === 'video' && (r.fileName || isBlobUrl(src))) { tab?.close(); setVideoUrl({ src, nombre: r.fileName || r.title }); return; }
    if (r.type === 'image') { tab?.close(); setPreview({ src, name: r.fileName || r.title }); return; }
    if (String(src).startsWith('data:')) { tab?.close(); openDataUrl(src); return; }
    if (tab) tab.location = src;
    else window.open(src, '_blank', 'noopener');
  };

  // `<a download href>` can't wait for a round trip, so fetch the fresh URL
  // first and then hand it to a throwaway anchor.
  const download = async (r) => {
    const src = await freshResourceFileUrl(r);
    if (!src) return;
    const a = document.createElement('a');
    a.href = src;
    a.download = r.fileName || r.title || 'archivo';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const card = { background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12 };
  const btn = (bg, color) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, color, border: 'none', borderRadius: 8, padding: '.4rem .7rem', fontSize: '.74rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none' });

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.4rem', color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
            <GraduationCap size={22} style={{ color: 'var(--c-cyan,#00d4ff)' }} /> {es ? 'Entrenamiento del Equipo' : 'Team Training'}
          </h1>
          <p style={{ fontSize: '.82rem', color: 'var(--c-text2,#7aa3c0)', margin: '.35rem 0 0' }}>
            {es ? 'Videos, PDFs y documentos de entrenamiento para todo el equipo.' : 'Training videos, PDFs and documents for the whole team.'}
          </p>
        </div>
        <button type="button" onClick={() => { setForm(empty); setShowForm(s => !s); }} style={btn('var(--c-cyan,#00d4ff)', '#0a1628')}>
          <Plus size={16} /> {es ? 'Agregar material' : 'Add material'}
        </button>
      </div>

      {/* Upload form */}
      {showForm && (
        <div style={{ ...card, padding: '1rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '.7rem' }}>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={es ? 'Título (ej. Entrenamiento ACA 2026)' : 'Title (e.g. ACA Training 2026)'} className="dkt-inp" style={{ width: '100%', padding: '.55rem .7rem' }} />
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={es ? 'Descripción (opcional)' : 'Description (optional)'} rows={2} className="dkt-inp" style={{ width: '100%', padding: '.55rem .7rem', resize: 'vertical' }} />
          {/* Compania y tema (Gabriel De Silva, 2026-08-26). Los dos se
              ESCRIBEN, y lo escrito queda como sugerencia para la proxima vez
              (Lissette, mismo dia: nada de nombres predeterminados).
              Los dos son OPCIONALES: obligar a clasificar convertiria "subir un
              material" en un tramite, y lo que hoy se sube sin clasificar
              seguiria subiendose igual — solo que ahora nadie lo subiria. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '.5rem' }}>
            <div>
              <label className="dkt-input-lbl">{es ? 'Compañía / Aseguradora' : 'Company / Carrier'}</label>
              <input list="dkt-tt-carriers" value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))}
                placeholder={es ? 'Escribe la compañía…' : 'Type the company…'} className="dkt-inp" style={{ width: '100%', padding: '.55rem .7rem', boxSizing: 'border-box' }} />
              {/* `datalist` y no `select`: sugiere lo que la agencia ya usa —
                  sus polizas y sus materiales— pero nunca cierra la lista. */}
              <datalist id="dkt-tt-carriers">
                {carrierOptions.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="dkt-input-lbl">{es ? 'Tipo de entrenamiento' : 'Training type'}</label>
              <input list="dkt-tt-topics" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                placeholder={es ? 'Escribe el tipo…' : 'Type the training type…'} className="dkt-inp" style={{ width: '100%', padding: '.55rem .7rem', boxSizing: 'border-box' }} />
              <datalist id="dkt-tt-topics">
                {topicOptions.map(t => <option key={t} value={t} />)}
              </datalist>
              {/* La primera vez no hay nada que sugerir, y un campo en blanco
                  sin explicacion se deja en blanco. Se dice de donde saldran. */}
              <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: 3 }}>
                {topicOptions.length
                  ? (es ? `Se guardan solos: ya hay ${topicOptions.length}.` : `Saved as you go: ${topicOptions.length} so far.`)
                  : (es ? 'El que escribas queda guardado para la próxima.' : 'What you type is saved for next time.')}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            {[{ id: 'video', es: '🎬 Video (link)', en: '🎬 Video (link)' }, { id: 'pdf', es: '📕 PDF / Documento', en: '📕 PDF / Document' }, { id: 'image', es: '🖼️ Imagen', en: '🖼️ Image' }].map(o => (
              <button key={o.id} type="button" onClick={() => setForm(f => ({ ...f, type: o.id, fileData: '', fileName: '', fileUrl: '' }))}
                style={{ ...btn(form.type === o.id ? 'rgba(0,212,255,.15)' : 'var(--c-surface2,#112538)', form.type === o.id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)'), border: '1px solid ' + (form.type === o.id ? 'rgba(0,212,255,.4)' : 'var(--c-border,#1a3550)') }}>
                {es ? o.es : o.en}
              </button>
            ))}
          </div>
          {form.type === 'video' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
              <input ref={videoFileRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
              <button type="button" disabled={uploading} onClick={() => videoFileRef.current?.click()} style={{ ...btn('var(--c-surface2,#112538)', 'var(--c-cyan,#00d4ff)'), border: '1px dashed var(--c-cyan,#00d4ff)', justifyContent: 'center' }}>
                <Upload size={15} /> {form.fileName ? `✓ ${form.fileName}` : (es ? 'Subir video desde mi computadora' : 'Upload video from my computer')}
              </button>
              <div style={{ textAlign: 'center', fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>{es ? '— o pega un link —' : '— or paste a link —'}</div>
              <input value={isBlobUrl(form.fileUrl) ? '' : form.fileUrl} onChange={e => setForm(f => ({ ...f, fileUrl: e.target.value, fileName: '', fileSize: 0 }))} placeholder="https://youtube.com/... · https://vimeo.com/... · Drive/Loom" className="dkt-inp" style={{ width: '100%', padding: '.55rem .7rem' }} />
            </div>
          ) : (
            <div>
              <input ref={fileRef} type="file" accept={form.type === 'image' ? 'image/*' : '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,image/*'} style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
              <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} style={{ ...btn('var(--c-surface2,#112538)', 'var(--c-text2,#7aa3c0)'), border: '1px dashed var(--c-border,#1a3550)' }}>
                <Upload size={15} /> {form.fileName ? `✓ ${form.fileName}` : (es ? 'Subir archivo' : 'Upload file')}
              </button>
            </div>
          )}
          {uploading && (
            <div>
              <div style={{ fontSize: '.72rem', color: 'var(--c-cyan,#00d4ff)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid var(--c-cyan,#00d4ff)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'dkt-spin .7s linear infinite' }} /> {es ? 'Subiendo…' : 'Uploading…'} {Math.round(progress * 100)}%
              </div>
              <div style={{ height: 6, background: 'var(--c-surface2,#112538)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.round(progress * 100)}%`, background: 'var(--c-cyan,#00d4ff)', transition: 'width .15s' }} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '.5rem' }}>
            <button type="button" onClick={save} style={{ ...btn('var(--c-cyan,#00d4ff)', '#0a1628'), flex: 1, justifyContent: 'center' }}>{es ? 'Guardar' : 'Save'}</button>
            <button type="button" onClick={() => { setShowForm(false); setForm(empty); }} style={btn('var(--c-surface2,#112538)', 'var(--c-text2,#7aa3c0)')}>{es ? 'Cancelar' : 'Cancel'}</button>
          </div>
        </div>
      )}

      {/* Buscar + filtros. Los desplegables solo salen cuando hay algo que
          filtrar: con dos materiales sin clasificar serian dos controles que
          no hacen nada. */}
      {training.length > 0 && (
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={es ? 'Buscar entrenamiento…' : 'Search training…'} className="dkt-inp" style={{ flex: '1 1 240px', maxWidth: 380, padding: '.5rem .75rem' }} />
          {carriersPresentes.length > 1 && (
            <select value={filterCarrier} onChange={e => setFilterCarrier(e.target.value)} className="dkt-inp"
              style={{ width: 'auto', padding: '.5rem .6rem', fontSize: '.78rem', cursor: 'pointer' }}>
              <option value="all">{es ? '🏢 Todas las compañías' : '🏢 All companies'}</option>
              {carriersPresentes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {temasPresentes.length > 1 && (
            <select value={filterTopic} onChange={e => setFilterTopic(e.target.value)} className="dkt-inp"
              style={{ width: 'auto', padding: '.5rem .6rem', fontSize: '.78rem', cursor: 'pointer' }}>
              <option value="all">{es ? '🎓 Todos los tipos' : '🎓 All types'}</option>
              {temasPresentes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          {(filterCarrier !== 'all' || filterTopic !== 'all' || search) && (
            <button type="button" onClick={() => { setFilterCarrier('all'); setFilterTopic('all'); setSearch(''); }}
              style={btn('var(--c-surface2,#112538)', 'var(--c-text2,#7aa3c0)')}>
              <X size={12} /> {es ? 'Limpiar' : 'Clear'}
            </button>
          )}
          <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', fontWeight: 700 }}>
            {items.length} {es ? (items.length === 1 ? 'material' : 'materiales') : (items.length === 1 ? 'item' : 'items')}
          </span>
        </div>
      )}

      {/* Grid */}
      {items.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '3rem 1rem', borderStyle: 'dashed' }}>
          <GraduationCap size={32} style={{ color: 'var(--c-border2,#1f3f60)', marginBottom: '.6rem' }} />
          {/* "No hay materiales" y "tu filtro no encuentra nada" son cosas
              DISTINTAS y se leen igual si las dos dicen lo mismo: el vacio es
              el peor mensaje de error que existe, porque nadie lo reporta —
              se lo explica a si mismo. Con filtros puestos, la pantalla dice
              que fue el filtro y ofrece quitarlo. */}
          {training.length > 0 ? (
            <>
              <div style={{ fontSize: '.9rem', color: 'var(--c-text2,#7aa3c0)' }}>{es ? 'Ningún material coincide con el filtro.' : 'No material matches the filter.'}</div>
              <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', marginTop: 5 }}>
                {es ? `Hay ${training.length} en total.` : `There are ${training.length} in total.`}
              </div>
              <button type="button" onClick={() => { setFilterCarrier('all'); setFilterTopic('all'); setSearch(''); }}
                style={{ ...btn('var(--c-cyan,#00d4ff)', '#0a1628'), marginTop: '.8rem' }}>
                <X size={13} /> {es ? 'Quitar filtros' : 'Clear filters'}
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: '.9rem', color: 'var(--c-text2,#7aa3c0)' }}>{es ? 'Aún no hay entrenamientos.' : 'No training materials yet.'}</div>
              <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', marginTop: 5 }}>{es ? 'Toca "Agregar material" para subir el primero.' : 'Tap "Add material" to upload the first one.'}</div>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: '.75rem' }}>
          {items.map(r => (
            <div key={r.id} style={{ ...card, padding: '.85rem', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.5rem' }}>
                <span style={{ fontSize: '1.4rem' }}>{typeIcon(r.type)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.86rem', color: 'var(--c-white,#eef6ff)', wordBreak: 'break-word' }}>{r.title}</div>
                  {r.description && <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 2 }}>{r.description}</div>}
                  {/* Clasificar sin poder VER la clasificacion es trabajo
                      tirado: las dos etiquetas salen en la tarjeta. */}
                  {(r.carrier || topicOf(r)) && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
                      {r.carrier && <span style={{ fontSize: '.63rem', fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: 'rgba(0,212,255,.12)', color: 'var(--c-cyan,#00d4ff)', border: '1px solid rgba(0,212,255,.3)' }}>{r.carrier}</span>}
                      {topicOf(r) && <span style={{ fontSize: '.63rem', fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: 'rgba(167,139,250,.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,.3)' }}>{topicOf(r)}</span>}
                    </div>
                  )}
                  {r.fileSize ? <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>{fmtSize(r.fileSize)}</div> : null}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginTop: 'auto' }}>
                <button type="button" onClick={() => view(r)} style={btn('rgba(0,212,255,.1)', 'var(--c-cyan,#00d4ff)')}><Eye size={13} /> {es ? 'Ver' : 'View'}</button>
                {(r.fileData || ((isBlobUrl(r.fileUrl) || String(r.fileUrl || '').startsWith('data:')) && r.fileName)) && (
                  <button type="button" onClick={() => download(r)} style={btn('var(--c-surface2,#112538)', 'var(--c-text2,#7aa3c0)')}><Download size={13} /> {es ? 'Descargar' : 'Download'}</button>
                )}
                <button type="button" onClick={() => del(r)} style={btn('var(--c-surface2,#112538)', 'var(--c-red,#ff3d6b)')}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded-video player */}
      {videoUrl && (
        <div onClick={() => setVideoUrl(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.6rem', maxWidth: '92vw' }}>
            <VisorDeVideo src={videoUrl.src} nombre={videoUrl.nombre} lang={lang} style={{ maxWidth: '92vw', maxHeight: '82vh', borderRadius: 8 }} />
            <button type="button" onClick={() => setVideoUrl(null)} style={btn('var(--c-surface2,#112538)', 'var(--c-text,#c8dff0)')}><X size={14} /> {es ? 'Cerrar' : 'Close'}</button>
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.6rem' }}>
            <img src={preview.src} alt={preview.name} style={{ maxWidth: '92vw', maxHeight: '80vh', borderRadius: 8, objectFit: 'contain' }} />
            <div style={{ display: 'flex', gap: '.5rem' }}>
              <a href={preview.src} download={preview.name || 'image.png'} style={btn('var(--c-cyan,#00d4ff)', '#06121f')}><Download size={14} /> {es ? 'Descargar' : 'Download'}</a>
              <button type="button" onClick={() => setPreview(null)} style={btn('var(--c-surface2,#112538)', 'var(--c-text,#c8dff0)')}><X size={14} /> {es ? 'Cerrar' : 'Close'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
